// NEW_FILE_JUSTIFIED: New messages feature component

import { memo, useRef, useEffect, useCallback } from 'react';
import { Star, Phone, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MessageBubble } from '@/components/messages/MessageBubble';
import { MessageComposer } from '@/components/messages/MessageComposer';
import { TelegramMessageActions } from '@/components/messages/TelegramMessageActions';
import { WhatsAppMessageActions } from '@/components/messages/WhatsAppMessageActions';
import { AiReplyButton } from '@/components/aiReply/AiReplyButton';
import { AiDraftBubble } from '@/components/aiReply/AiDraftBubble';
import { useAiReplyDrafts } from '@/hooks/useAiReplyDrafts';
import type { IChatPanelProps } from '@/@types/messages';
import type { IAiReplyDraft } from '@/@types/aiReply';

export const ChatPanel = memo(function ChatPanel({
  contact,
  messages,
  onSendMessage,
  onSendFile,
  isSendingFile,
  activeChannel,
  onChannelChange,
  platform,
  onReaction,
  onDeleteMessage,
  onReplyMessage,
  replyContext,
  onCancelReply,
  onCreateLead,
}: IChatPanelProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(false);

  const { pendingDraft, handleCancel, handleMarkSent } = useAiReplyDrafts(
    contact.id,
    platform
  );

  const handleDraftSend = useCallback(
    async (draftId: IAiReplyDraft['_id'], text: string) => {
      await onSendMessage(text);
      await handleMarkSent(draftId, text);
    },
    [onSendMessage, handleMarkSent]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: isMountedRef.current ? 'smooth' : 'instant',
    });
    isMountedRef.current = true;
  }, [messages]);

  return (
    <div className="flex flex-1 flex-col min-w-0 bg-msg-panel-bg">
      {/* Chat Header */}
      <div
        className={cn(
          'flex h-16 items-center justify-between px-5',
          'border-b border-msg-panel-border bg-msg-panel-bg'
        )}
      >
        {/* Left side: avatar + info */}
        <div className="flex items-center gap-3">
          {contact.profilePicUrl ? (
            <img
              src={contact.profilePicUrl}
              alt=""
              aria-hidden="true"
              className="size-10 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: contact.avatarColor }}
            >
              <span className="text-sm font-semibold text-primary-foreground">
                {contact.initials}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-foreground">{contact.name}</span>
            {contact.chatType === 'group' ? (
              <span className="text-xs text-muted-foreground">Group</span>
            ) : (
              <div className="flex items-center gap-1.5">
                <div className="size-2 rounded-full bg-primary" aria-hidden="true" />
                <span className="text-xs text-muted-foreground">Active now</span>
              </div>
            )}
          </div>
        </div>

        {/* Right side: action buttons */}
        <TooltipProvider delayDuration={300}>
        <div className="flex gap-2 items-center">
          <AiReplyButton
            contactIdentifier={contact.id}
            platform={platform}
            contactName={contact.name}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Star conversation"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <Star size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent>Star conversation</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Call contact"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <Phone size={16} />
              </button>
            </TooltipTrigger>
            <TooltipContent>Call</TooltipContent>
          </Tooltip>
          {onCreateLead && (
            <button
              type="button"
              onClick={onCreateLead}
              aria-label="Create lead from conversation"
              className={cn(
                'flex h-9 items-center gap-1.5 rounded-lg px-3',
                'bg-muted text-muted-foreground text-xs font-medium',
                'hover:bg-primary hover:text-primary-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'transition-colors'
              )}
            >
              <UserPlus size={14} aria-hidden="true" />
              Create lead
            </button>
          )}
        </div>
        </TooltipProvider>
      </div>

      {/* Messages Area — native scroll to avoid Radix ScrollArea display:table wrapper
           breaking width constraints in flex layout */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-4 p-5">
          {messages.map(message => {
            const bubble = <MessageBubble key={message.id} message={message} />;

            if (platform === 'telegram') {
              return (
                <TelegramMessageActions
                  key={message.id}
                  message={message}
                  onReaction={onReaction}
                  onDeleteMessage={onDeleteMessage}
                  onReplyMessage={onReplyMessage}
                >
                  {bubble}
                </TelegramMessageActions>
              );
            }

            return (
              <WhatsAppMessageActions
                key={message.id}
                message={message}
                onReaction={onReaction}
                onDeleteMessage={onDeleteMessage}
                onReplyMessage={onReplyMessage}
              >
                {bubble}
              </WhatsAppMessageActions>
            );
          })}
          {/* AI Draft Bubble — shown inside scroll area so long drafts don't push buttons off screen */}
          {pendingDraft !== null && (
            <AiDraftBubble
              draft={pendingDraft}
              onSend={handleDraftSend}
              onCancel={handleCancel}
            />
          )}
          <div ref={bottomRef} aria-hidden="true" />
        </div>
      </div>

      {/* Message Composer */}
      <MessageComposer
        onSendMessage={onSendMessage}
        onSendFile={onSendFile}
        isSendingFile={isSendingFile}
        activeChannel={activeChannel}
        onChannelChange={onChannelChange}
        replyContext={replyContext}
        onCancelReply={onCancelReply}
      />
    </div>
  );
});
